import React, {useEffect, useMemo, useState} from "react"
import SearchableSelect, {SearchableSelectOption} from "."
import {UserDataTypes} from "../../../types/user-data-types"
import api, {apiV2} from "../../../api/axiosConfig"
import {PaginatedResponse} from "../../../types/PaginatedResponse"
import PeopleIcon from "../../../assets/icons/icon-person.svg";
import {Client} from "../../../types/userManagement";
import ClientSelect from "./ClientSelect";
import {usePlatformAPI} from "../../../api/PlatformProvider";
import {createUserApi} from "../../../api/platform/users";

type UserSelectParams = {
  clientID?: string | null;
  departmentID?: string | null;
  accessLevel?: string;
  excludeIDs?: string[];
  multiple?: boolean;
  onSelectionChange?: (users: UserDataTypes[]) => void
  users: UserDataTypes[],
  initialUserIDs?: string[],
  disabled?: boolean
  noClientOnChange?: boolean;
}

function UserSelect({
                      users,
                      clientID,
                      departmentID,
                      accessLevel,
                      excludeIDs,
                      multiple,
                      onSelectionChange,
                      initialUserIDs,
                      disabled,
                      noClientOnChange,
                    }: UserSelectParams) {

  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<SearchableSelectOption<UserDataTypes>[]>([])
  const [searchStr, setSearchStr] = useState("")

  const platformClient = usePlatformAPI()
  const userApi = useMemo(() => createUserApi(platformClient), [platformClient])

  const fetchUsers = async () => {
    setLoading(true)
    try {

      if (accessLevel) {
        const [data, err] = await userApi.list(accessLevel, searchStr, excludeIDs)
        if (!err && data) {
          setOptions(data.map((u) => ({
            label: u.name,
            value: { id: u.id, email: u.email, profile: { firstName: u.name, lastName: "", middleNames: null } } as unknown as UserDataTypes,
            key: u.id,
          })))
        }
      } else {
        if (initialUserIDs && initialUserIDs.length > 0) {
          const initialUsers = await apiV2.get<{ items: UserDataTypes[] }>(`/users?id=${initialUserIDs.join(",")}`);
          if (onSelectionChange) {
            onSelectionChange(initialUsers.data.items)
          }
        }

        const res = await apiV2.get<PaginatedResponse<UserDataTypes>>("/users", {
          params: {
            clientID,
            departmentID,
            search: searchStr,
            rpp: 200
          }
        })
        setOptions(res.data.items.map((u) => ({
          label: `${u.profile.firstName} ${u.profile.lastName}`,
          value: u,
          key: u.id
        })))
      }

    } catch (e) {
      console.error(e)
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchUsers()
  }, [clientID, departmentID, accessLevel, searchStr, JSON.stringify(excludeIDs)])

  useEffect(() => {
    if (!noClientOnChange) {
      onChange([])
    }
  }, [clientID, departmentID, noClientOnChange])

  const onChange = (selected: SearchableSelectOption<UserDataTypes>[]) => {
    if (onSelectionChange) {
      onSelectionChange(selected.map(s => s.value))
    }
  }

  const onSearch = (value: string) => {
    setSearchStr(value)
  }

  return (<SearchableSelect
      icon={PeopleIcon}
      label={multiple ? "Users" : "User"}
      selected={users.map(u => ({
        label: `${u.profile.firstName} ${u.profile.lastName}`,
        value: u,
        key: u.id
      }))}
      search={true}
      multiple={multiple ? true : false}
      options={options}
      localSearch={false}
      onChange={onChange}
      onSearch={onSearch}
      loading={loading}
      disabled={disabled}
  />)
}

export default UserSelect


type UserSelectSingleProps = {
  user?: UserDataTypes;
  onChange?: (selected: UserDataTypes|undefined) => void;
  clientID?: string | null;
  departmentID?: string | null;
  accessLevel?: string;
  excludeIDs?: string[];
  disabled?: boolean;
}

export const UserSelectSingle: React.FC<UserSelectSingleProps> = (props) =>
{

  const onChange = (users: UserDataTypes[]) => {
    if (!props.onChange) return;

    if(users.length == 0){
      props.onChange(undefined)
      return
    }

    props.onChange(users[0])
  }

  return <UserSelect disabled={props.disabled} multiple={false} onSelectionChange={onChange} clientID={props.clientID} accessLevel={props.accessLevel} excludeIDs={props.excludeIDs} users={props.user ? [props.user] : []} initialUserIDs={props.accessLevel ? undefined : (props.user ? [props.user.id] : undefined)}/>
}

